import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import headerBg from "./assets/picOne.png";
import { Newspaper, FileText, Megaphone, BookOpen } from "lucide-react";
import { apiUrl } from "../config/api";

const themeColor = "#FF5400";

const getIcon = (type) => {
  switch (type) {
    case "Projects":
      return <FileText color={themeColor} size={24} />;
    case "Newsletters":
      return <Newspaper color={themeColor} size={24} />;
    case "Press":
      return <Megaphone color={themeColor} size={24} />;
    case "Story":
      return <BookOpen color={themeColor} size={24} />;
    default:
      return <Newspaper color={themeColor} size={24} />;
  }
};

const getImageUrl = (image) => {
  if (!image) return "";
  if (/^https?:\/\//i.test(image)) return image;

  return apiUrl(image);
};

const isPdfFile = (path) => {
  return path && path.toLowerCase().endsWith(".pdf");
};

const normalizeHomeSection = (item, section) => {
  const resolvedSection = item.section || section;
  const isProject = resolvedSection === "projects";

  const filePath =
    item.image || item.image_url || item.thumbnail || item.logo;

  return {
    id: item.id,
    detailSection: resolvedSection,
    title: item.heading || "Untitled",
    date: item.created_at || item.updated_at || "",
    type: isProject ? "Projects" : "Newsletters",

    thumbnail: getImageUrl(filePath),
    isPdf: isPdfFile(filePath),

    thumbnailStyle: {
      height: "400px",
      objectFit: "cover",
    },

    caption: (item.caption || "").trim(),
    content: item.content || "",
    url_link: item.url_link || null,
    order: item.order ?? item.id ?? 0,
  };
};

const parseStoryId = (storyId) => {
  if (!storyId) return null;

  const projectMatch = storyId.match(/^project(\d+)$/i);
  if (projectMatch) {
    return { section: "projects", id: projectMatch[1] };
  }

  const carouselMatch = storyId.match(/^(?:story|carousel)?(\d+)$/i);
  if (carouselMatch) {
    return { section: "carousel", id: carouselMatch[1] };
  }

  const mediaMatch = storyId.match(/^media(\d+)$/i);

if (mediaMatch) {
  return {
    section: "media_publications",
    id: mediaMatch[1],
  };
}

  return null;
};

const sortByRecent = (a, b) => {
  const dateA = a.date ? new Date(a.date) : new Date(0);
  const dateB = b.date ? new Date(b.date) : new Date(0);

  return dateB - dateA || (b.order || 0) - (a.order || 0);
};

const fetchJson = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Request failed: ${url}`);

  return res.json();
};

const MediaPublications = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const mediaTypes = ["All", "Newsletters", "Projects"];
  const [filterType, setFilterType] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [sidebarItems, setSidebarItems] = useState([]);
  const [detail, setDetail] = useState(null);

  const routeTarget = useMemo(() => parseStoryId(storyId), [storyId]);

  useEffect(() => {
    let mounted = true;

    const fetchSidebar = async () => {
      const [carouselResult, projectsResult, mediaResult] = await Promise.allSettled([
        fetchJson(apiUrl("/api/home-sections/carousel")),
        fetchJson(apiUrl("/api/home_sections/projects")),
        fetchJson(apiUrl("/api/home_sections/media_publications")),
      ]);

      const carousel =
        carouselResult.status === "fulfilled" ? carouselResult.value : [];
      const projects =
        projectsResult.status === "fulfilled" ? projectsResult.value : [];
        const media =
         mediaResult.status === "fulfilled" ? mediaResult.value : [];

      const newsletters = Array.isArray(carousel)
        ? carousel.map((item) => normalizeHomeSection(item, "carousel"))
        : [];
      const projectItems = Array.isArray(projects)
        ? projects.map((item) => normalizeHomeSection(item, "projects"))
        : [];

        const mediaItems = Array.isArray(media)
    ? media.map((item) => normalizeHomeSection(item, "media_publications"))
    : [];

      if (mounted) {
        setSidebarItems([...newsletters, ...projectItems, ...mediaItems].sort(sortByRecent));
      }
    };

    fetchSidebar();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredStories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return sidebarItems
      .filter((pub) => filterType === "All" || pub.type === filterType)
      .filter((pub) => {
        if (!term) return true;

        return (
          pub.title.toLowerCase().includes(term) ||
          pub.caption.toLowerCase().includes(term) ||
          pub.content.toLowerCase().includes(term)
        );
      })
      .filter((pub) => {
        if (!filterDate) return true;

        return (pub.date || "").slice(0, 10) === filterDate;
      })
      .sort(sortByRecent);
  }, [filterDate, filterType, searchTerm, sidebarItems]);

  const defaultTarget = useMemo(() => {
    const fallback = filteredStories[0];
    if (!fallback) return null;

    return {
      section: fallback.detailSection,
      id: fallback.id,
    };
  }, [filteredStories]);

  const detailTarget = routeTarget || defaultTarget;

  useEffect(() => {
    let mounted = true;

    const fetchDetail = async () => {
      if (!detailTarget) {
        setDetail(null);
        return;
      }

      try {
        const data = await fetchJson(
          apiUrl(`/api/home_sections/${detailTarget.section}/${detailTarget.id}`)
        );

        if (mounted) {
          setDetail(normalizeHomeSection(data, detailTarget.section));
        }
      } catch (err) {
        const sidebarFallback = sidebarItems.find(
          (item) =>
            item.detailSection === detailTarget.section &&
            String(item.id) === String(detailTarget.id)
        );

        if (mounted) {
          setDetail(sidebarFallback || null);
        }
      }
    };

    fetchDetail();

    return () => {
      mounted = false;
    };
  }, [detailTarget, sidebarItems]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [storyId, detail?.id, detail?.detailSection]);

  const handleStoryClick = (story) => {
    const urlId =
      story.detailSection === "projects" ? `project${story.id}` : `story${story.id}`;

    navigate(`/mediaPublications/${urlId}`);
  };

  const isActiveStory = (story) => {
    if (!detailTarget) return false;

    return (
      story.detailSection === detailTarget.section &&
      String(story.id) === String(detailTarget.id)
    );
  };

  const thumbnailStyle = detail?.thumbnailStyle || {};

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#f7f7f8" }}>
      {/* Hero Section */}
      <section
        style={{
          backgroundImage: `url(${headerBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
          height: "40vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "rgba(0,0,0,0.55)",
            padding: "40px",
            textAlign: "center",
            color: "#fff",
            width: "90%",
            maxWidth: "1200px",
            borderRadius: "12px",
            borderRight: `4px solid ${themeColor}`,
            borderLeft: `4px solid ${themeColor}`,
          }}
        >
          <h1 style={{ fontSize: "2.5rem", fontWeight: "700", marginBottom: "10px" }}>Stories</h1>
          <p style={{ fontSize: "1rem", maxWidth: "700px", margin: "0 auto" }}>
            {/* Explore recent stories by MBS, with full details when selected. */}
          </p>
        </div>
      </section>

      {/* Filters & Main Content */}
      <section style={{ display: "flex", maxWidth: "1400px", margin: "40px auto", gap: "30px", flexWrap: "wrap" }}>
        {/* Left Column */}
        <div style={{ flex: "1 1 300px", minWidth: "280px", maxHeight: "500px", overflowY: "auto" }}>
          <div style={{ display: "flex", gap: "8px", marginBottom: "15px", flexWrap: "wrap" }}>
            {mediaTypes.map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  background: filterType === type ? themeColor : "#eee",
                  color: filterType === type ? "#fff" : "#333",
                  fontWeight: 600,
                }}
              >
                {type}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: "20px" }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #ccc", marginBottom: "10px" }}
            />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #ccc" }}
            />
          </div>

          {filteredStories.map(story => {
            const active = isActiveStory(story);

            return (
              <div
                key={`${story.detailSection}-${story.id}`}
                onClick={() => handleStoryClick(story)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "12px 15px",
                  marginBottom: "10px",
                  borderRadius: "12px",
                  background: active ? themeColor : "#fff",
                  color: active ? "#fff" : "#333",
                  cursor: "pointer",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                  transition: "0.3s",
                }}
              >
                {getIcon(story.type)}
                <div>
                  <h4 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600, color: active ? "#fff" : "#000" }}>
                    {story.title}
                  </h4>
                  <p style={{ margin: 0, fontSize: "0.8rem", opacity: 0.7 }}>
                    {story.date ? new Date(story.date).toLocaleDateString() : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Column */}
        <div
          ref={contentRef}
          style={{ flex: "2 1 600px", background: "#fff", borderRadius: "20px", padding: "30px", boxShadow: "0 15px 40px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column", }}
        >
          {detail && (
            <>
              <h2 style={{ fontSize: "1.8rem", fontWeight: "700", color: "#000", marginBottom: "15px" }}>{detail.title}</h2>
              <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "20px" }}>
                {detail.date ? new Date(detail.date).toLocaleDateString() : ""} {detail.type ? ` - ${detail.type}` : ""}
              </p>

              {/* Render image only if exists. */}

              {detail.thumbnail && (
  <>
    {detail.isPdf ? (
      <iframe
        src={detail.thumbnail}
        title={detail.title}
        width="100%"
        height="800"
        style={{
          border: "1px solid #ddd",
          borderRadius: "12px",
          marginBottom: "20px",
        }}
      />
    ) : (
      <>
        <img
          src={detail.thumbnail}
          alt={detail.title}
          style={{
            width: "100%",
            borderRadius: "12px",
            marginBottom: "10px",
            height: thumbnailStyle.height || "400px",
            maxHeight: "60vh",
            objectFit: thumbnailStyle.objectFit || "cover",
            objectPosition: thumbnailStyle.objectPosition || "center",
          }}
        />

        {detail.caption && (
          <p
            style={{
              fontSize: "0.9rem",
              color: "#666",
              fontStyle: "italic",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            {detail.caption}
          </p>
        )}
      </>
    )}
  </>
)}
              {/* {detail.thumbnail && (
                <>
                  <img
                    src={detail.thumbnail}
                    alt={detail.title}
                    style={{
                      width: "100%",
                      borderRadius: "12px",
                      marginBottom: "10px",
                      height: thumbnailStyle.height || "400px",
                      maxHeight: "60vh",
                      objectFit: thumbnailStyle.objectFit || "cover",
                      objectPosition: thumbnailStyle.objectPosition || "center",
                    }}
                  />
                  {detail.caption && (
                    <p style={{ fontSize: "0.9rem", color: "#666", fontStyle: "italic", textAlign: "center", marginBottom: "20px" }}>
                      {detail.caption}
                    </p>
                  )}
                </>
              )} */}

              <div style={{ fontSize: "1rem", lineHeight: "1.6", color: "#444", overflowY: "auto", flex: 1, paddingRight: "10px" }}>
                {detail.content.split("\n").map((line, i) => <p key={i}>{line}</p>)}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default MediaPublications;
